import { error } from '@/utils/error';
import { getHttpService } from './HttpService';

interface CollectivityResponse {
    data: CollectivityData;
}
interface CollectivityData {
    afspraaknummer: string;
    naam: string;
    vergoedingenSet: string;
    logoUrl: string;
    isAdministratiefContract: boolean;
    propositie: string;
    isBorderel: boolean;
    betaaltermijnSoort: string;
    telefoonNummer?: null;
    doelgroep: string;
    commercieleNaam: string;
    usps?: string[] | null;
    bvCollectiviteitvoordelen: CollectivityBenefits;
    avCollectiviteitvoordelen: CollectivityBenefits;
    tvCollectiviteitvoordelen: CollectivityBenefits;
    verplichtEigenRisico: number;
    ingangsdatum: string;
}
interface CollectivityBenefits {
    producten?: CollectivityProducts[] | null;
    maxDiscountPercentage: number;
    discountPercentageVaries: boolean;
    kortingsbedrag: number;
}
interface CollectivityProducts {
    naam: string;
    verkorteOmschrijving: string;
    premiebekendmakingUitgesteld: boolean;
    prijs: number;
}

interface CollectivitiesResponse {
    data: {
        isSucces: boolean;
        resultaten: CollectivitiesData[];
        errorCode?: null;
        statusCode: number;
        loginAlsKlantSessieNotActive: boolean;
        content?: null;
    };
}
interface CollectivitiesData {
    collectiviteitNummer: string;
    merk: string;
    propositie: string;
    naam: string;
    plaats: string;
    beginDatum: string;
    eindDatum?: null;
    logo: string;
    isBlocked: boolean;
    administratiefContract: boolean;
    blokkadeTitel?: null;
    blokkadeTekst?: null;
}

interface AllianceResponse {
    data: {
        allianties: AllianceData[];
        isSucces: boolean;
        errorCode?: null;
        statusCode: number;
        loginAlsKlantSessieNotActive: boolean;
        content?: null;
    };
}
interface AllianceData {
    id: number;
    propositie: string;
    naam: string;
    naamEn: string;
    volgorde: number;
    opmerkingTitel?: string | null;
    opmerkingTekst?: string | null;
    collectiviteiten?: AllianceCollectivities[] | null;
}
interface AllianceCollectivities {
    collectiviteitNummer: string;
    naam?: string | null;
    plaats?: string | null;
    logo?: string | null;
    volgordeCollectiviteit: number;
}

interface UspResponse {
    data: UspData;
}
interface UspData {
    afspraaknummer: string;
    naam: string;
    usps: string[];
}

const HttpService = getHttpService();

const getCollectivity = async ({
    brand,
    collectivityNumber
}: {
    brand: string;
    collectivityNumber: string;
}): Promise<CollectivityData> => {
    const path = '/clientcontroller/salesfunnelclient/collectiviteit';
    const payload = {
        Propositie: brand ?? 'BAP',
        Collectiviteitnummer: collectivityNumber
    };

    const { data } = (await HttpService.post(path, payload)) as CollectivityResponse;

    if (!data) {
        error('Client controller to fetch collectivity call was unsuccessful', {
            path,
            payload: JSON.stringify(payload)
        });
    }

    return data;
};

const getCollectivities = async ({
    brand,
    searchterm
}: {
    brand: string;
    searchterm: string;
}): Promise<CollectivitiesData[]> => {
    const path = '/clientcontroller/collectiviteitzoekerclient/Zoeken';
    const payload = {
        Propositie: brand ?? 'BAP',
        Versie: 'SF', // Versie opties zijn: SF | KD | <leeg>
        Zoektekst: searchterm
    };

    const { data } = (await HttpService.post(path, payload)) as CollectivitiesResponse;

    if (data.isSucces === false) {
        error('Client controller to search for collectivity call was unsuccessful', {
            path,
            payload: JSON.stringify(payload)
        });
    }

    return data.resultaten;
};

const getAlliances = async ({ brand }: { brand: string }): Promise<AllianceData[]> => {
    const path = `/clientcontroller/collectiviteitzoekerclient/allianties?propositie=${brand}`;

    const { data } = (await HttpService.get(path)) as AllianceResponse;

    if (data.isSucces === false) {
        error('Client controller to fetch alliances call was unsuccessful', {
            path
        });
    }

    return data.allianties;
};

const getUsps = async ({
    brand,
    collectivityNumber
}: {
    brand: string;
    collectivityNumber: string;
}): Promise<UspData> => {
    const path = '/clientcontroller/salesfunnelclient/collectiviteitUsps';
    const payload = {
        Propositie: brand ?? 'BAP',
        Collectiviteitnummer: collectivityNumber
    };

    const { data } = (await HttpService.post(path, payload)) as UspResponse;

    if (!data) {
        error('Client controller to fetch USPs call was unsuccessful', {
            path,
            payload: JSON.stringify(payload)
        });
    }

    return data;
};

const removeCollectivityCookies = async ({ brand }: { brand: string }): Promise<void> => {
    const path = '/clientcontroller/salesfunnelclient/removecollectiviteitcookies';
    const payload = {
        Propositie: brand ?? 'BAP'
    };

    const { data } = await HttpService.post(path, payload);

    if (!data) {
        error('Client controller to remove collectivity cookies call was unsuccessful', {
            path,
            payload: JSON.stringify(payload)
        });
    }

    return;
};

const setCookie = async ({
    brand,
    collectivityNumber
}: {
    brand: string;
    collectivityNumber: number;
}): Promise<void> => {
    const path = '/clientcontroller/salesfunnelclient/setcollectiviteitcookies';
    const payload = {
        Propositie: brand ?? 'BAP',
        Collectiviteitnummer: collectivityNumber
    };

    HttpService.post(path, payload);
};

export default {
    getCollectivity,
    getCollectivities,
    getAlliances,
    getUsps,
    setCookie,
    removeCollectivityCookies
};
